/**
 * The console's live feed. Opens `GET /ops/stream` (Task 7's SSE endpoint)
 * and applies its five event types directly to the store — `thread`,
 * `threads`, `presence`, `service`, `toast`. Nothing here is a decision;
 * every payload is exactly what the ops service already computed.
 *
 * RECONNECT. This used to attach no `error` handler at all, which meant the
 * feed died silently: `EventSource` retries a transport blip on its own, but a
 * non-2xx response (a 401 once the session token expires, chiefly) makes it
 * fire `error`, go to `readyState === CLOSED`, and stop forever. The pane then
 * kept rendering whatever it had last received, with nothing on screen saying
 * otherwise, until someone pressed F5 — which "fixed" it only by constructing a
 * new EventSource.
 *
 * Two things follow from owning the reconnect:
 *
 *  1. `/ops/stream` sends NO snapshot on connect. It only forwards mutations
 *     and the periodic presence tick. So a reconnect that just reopens the
 *     socket leaves the pane stale until the next tick happens to publish
 *     something. `onResync` fires after every successful REopen (never the
 *     first connect) so the caller can refetch immediately.
 *  2. The status is published to the store, because a feed that is quietly
 *     retrying looks identical to a feed that is working.
 */
import type { StoreApi, UseBoundStore } from 'zustand'
import type { ThreadDetail, ThreadSummary } from '@communicator/shared'
import { nextDelayMs } from '@communicator/shared'
import type { ConsoleStore, Peer, ServiceStatus, ToastTone } from '../store'
import { loadRuntimeConfig } from '../runtime-config'
import type { RefreshOutcome } from './sessionRefresh'

type Store = UseBoundStore<StoreApi<ConsoleStore>>

export interface StreamDeps {
  /** Defaults to the global `EventSource`. Tests must supply a fake — jsdom has none. */
  EventSourceImpl?: typeof EventSource
  /** Defaults to `${apiBase}/ops/stream` via loadRuntimeConfig(). */
  url?: string | (() => string | Promise<string>)
  /**
   * Called after each successful REOPEN, never after the first connect. The
   * feed carries no snapshot, so this is how the pane catches up on whatever
   * changed while it was disconnected instead of waiting for the next tick.
   */
  onResync?: () => void
  /** Injectable timers, so a test can drive reconnects without real delays. */
  setTimeoutImpl?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimeoutImpl?: (handle: ReturnType<typeof setTimeout>) => void
  /** Injectable jitter source for deterministic tests. */
  random?: () => number
  /**
   * Attempt a session refresh before reopening.
   *
   * EventSource surfaces no status code, so a 401 and a dropped socket are
   * indistinguishable here. Retrying an expired session forever would show
   * "reconnecting" to someone who is actually signed out — the very lie the
   * status indicator exists to prevent. Refreshing first turns the common case
   * (an 8h expiry) into a reconnect that simply works.
   *
   * THREE outcomes, not two. `refused` is the server rejecting the token and
   * is the only one that means signed out. `unavailable` is not reaching the
   * server — during a deploy, or on a flaky link — and must keep retrying.
   * Treating those the same is what dumped people to the sign-in screen every
   * time a container restarted.
   */
  refreshSession?: () => Promise<RefreshOutcome>
  /** Called when refresh reports a real sign-out, so the shell can show sign-in. */
  onSignedOut?: () => void
  /**
   * A thread this console had not seen before. Drives `p.sound` / `p.desktop`.
   * Fired only for genuinely new threads, never for updates to known ones —
   * otherwise every keystroke on an open conversation would chime.
   */
  onNewThread?: (thread: ThreadSummary) => void
}

export interface StreamHandle {
  close: () => void
}

function parse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export function subscribeStream(store: Store, deps: StreamDeps = {}): StreamHandle {
  let closed = false
  let source: EventSource | null = null
  let attempt = 0
  let hasConnectedOnce = false
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const setTimer = deps.setTimeoutImpl ?? ((fn, ms) => setTimeout(fn, ms))
  const clearTimer = deps.clearTimeoutImpl ?? ((h) => clearTimeout(h))

  async function resolveUrl(): Promise<string> {
    if (deps.url !== undefined) {
      return typeof deps.url === 'function' ? deps.url() : deps.url
    }
    const { apiBase } = await loadRuntimeConfig()
    return `${apiBase}/ops/stream`
  }

  function attach(es: EventSource): void {
    es.addEventListener('thread', (event) => {
      const data = parse<ThreadSummary | ThreadDetail>((event as MessageEvent).data)
      if (!data) return
      // Notify BEFORE the upsert, while the store still knows whether this
      // thread is new to us. Afterwards every thread looks like one we already
      // had, and the console would go permanently silent.
      const known = store.getState().threads.some((t) => t.id === data.id)
      store.getState().upsertThread(data)
      if (!known) deps.onNewThread?.(data as ThreadSummary)
    })

    es.addEventListener('threads', (event) => {
      const data = parse<{ threads: ThreadSummary[] }>((event as MessageEvent).data)
      if (!data) return
      // Every open thread on the floor, not this viewer's rail — the presence
      // tick broadcasts one payload to everyone. Stored as-is: the rail
      // filters at render (QueueRail's `visible`), so the store stays the
      // full picture the thread pane reads its live updates from.
      store.getState().setThreads(data.threads)
    })

    es.addEventListener('presence', (event) => {
      const data = parse<{ peers: Peer[] }>((event as MessageEvent).data)
      if (data) store.getState().setPeers(data.peers)
    })

    es.addEventListener('service', (event) => {
      const data = parse<Partial<ServiceStatus>>((event as MessageEvent).data)
      if (data) store.getState().setSvc(data)
    })

    es.addEventListener('toast', (event) => {
      const data = parse<{ text: string; tone?: ToastTone }>((event as MessageEvent).data)
      if (data) store.getState().pushToast(data)
    })

    // `open` is the only honest signal that the feed is actually live. Treating
    // "we called the constructor" as connected is what let a dead stream look
    // healthy for the whole of this bug's life.
    es.addEventListener('open', () => {
      if (closed) return
      attempt = 0
      store.getState().setStreamStatus('live')
      if (hasConnectedOnce) deps.onResync?.()
      hasConnectedOnce = true
    })

    es.addEventListener('error', () => {
      if (closed) return
      // ALWAYS take control. The previous version returned early when
      // readyState was CONNECTING, on the theory that the browser's own retry
      // should be left alone — but then a later error also triggered OUR retry,
      // so browser-native reconnection and ours both ran and each opened a
      // connection. Six accumulated in four minutes on the live deployment,
      // none ever closing, and the status indicator flickered as they raced.
      //
      // One owner is simpler and observable: close it, back off, reopen once.
      store.getState().setStreamStatus('reconnecting')
      es.close()
      scheduleReconnect()
    })
  }

  function scheduleReconnect(): void {
    if (closed || retryTimer !== null) return
    const delay = nextDelayMs(attempt, { random: deps.random })
    attempt += 1
    retryTimer = setTimer(() => {
      retryTimer = null
      void (async () => {
        // Only from the second attempt on. The first retry is far more likely
        // to be a blip than an expiry, and refreshing on every flicker would
        // rotate the token needlessly all day.
        if (attempt > 1 && deps.refreshSession) {
          const outcome = await deps.refreshSession()
          if (closed) return
          if (outcome === 'refused') {
            // Genuinely signed out. Retrying cannot succeed, and saying
            // "reconnecting" forever would be a lie told indefinitely.
            store.getState().setStreamStatus('closed')
            closed = true
            deps.onSignedOut?.()
            return
          }
          if (outcome === 'unavailable') {
            // The server is down or unreachable, which says nothing about the
            // session. Keep the backoff running and stay honest about the
            // state: reconnecting, because that is what we are doing.
            store.getState().setStreamStatus('reconnecting')
            scheduleReconnect()
            return
          }
        }
        void connect()
      })()
    }, delay)
  }

  async function connect(): Promise<void> {
    if (closed) return
    let url: string
    try {
      url = await resolveUrl()
    } catch {
      // Even the config fetch can fail during an outage; that is a retry, not
      // a permanent stop.
      store.getState().setStreamStatus('reconnecting')
      scheduleReconnect()
      return
    }
    if (closed) return

    // Close whatever we had before replacing it. Without this, every reconnect
    // leaked the previous EventSource — the connection stayed open server-side
    // (no completion ever logged) while we stopped listening to it.
    if (source) {
      try {
        source.close()
      } catch {
        // Already dead; nothing to do.
      }
      source = null
    }

    const EventSourceCtor = deps.EventSourceImpl ?? EventSource
    try {
      source = new EventSourceCtor(url, { withCredentials: true })
    } catch {
      store.getState().setStreamStatus('reconnecting')
      scheduleReconnect()
      return
    }
    attach(source)
  }

  store.getState().setStreamStatus('connecting')
  void connect()

  /**
   * COMING BACK TO THE APP.
   *
   * iOS suspends a backgrounded page and kills its connections, and the
   * browser does not reliably fire `error` on the EventSource when it does —
   * so we sat holding a dead socket, showing whatever the rail looked like
   * when the phone went to sleep, until something else happened to jostle it.
   *
   * That is why an installed app felt slower than Safari: a standalone app is
   * suspended harder and there is no tab-switching to knock it awake. The
   * server was publishing the whole time.
   *
   * Reconnecting outright rather than checking `readyState`: a zombie source
   * can still report OPEN, and a redundant reconnect costs one request while
   * a missed one costs every message until the next accident.
   *
   * `onResync` runs regardless, so the rail is correct the instant it is
   * looked at rather than a round trip later.
   */
  function wakeUp(): void {
    if (closed) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (retryTimer !== null) {
      clearTimer(retryTimer)
      retryTimer = null
    }
    attempt = 0
    source?.close()
    source = null
    deps.onResync?.()
    void connect()
  }

  const hasWindow = typeof window !== 'undefined'
  if (hasWindow) {
    document.addEventListener('visibilitychange', wakeUp)
    // A phone that changed network — van to office wifi — has the same dead
    // socket for the same reason.
    window.addEventListener('online', wakeUp)
  }

  return {
    close: () => {
      closed = true
      if (hasWindow) {
        document.removeEventListener('visibilitychange', wakeUp)
        window.removeEventListener('online', wakeUp)
      }
      if (retryTimer !== null) {
        clearTimer(retryTimer)
        retryTimer = null
      }
      source?.close()
      store.getState().setStreamStatus('closed')
    },
  }
}
